/**
 * The `desktop-launcher` namespace dictionaries: copy for the plugin
 * settings card (the `web-ui.plugin.item` seat) that edits the launcher
 * behavior and the shutdown behavior, and the floating shutdown button.
 */

/** Simplified Chinese dictionary (the key-set source of truth). */
export const zh = {
  'settings.title': '桌面启动器',
  'settings.description': '桌面图标创建与一键关机。',
  'settings.enabled': '启用插件',
  'settings.enabledHint': '关闭后不再提供桌面图标创建与关机按钮。',
  'settings.announceToAgent': '向 Agent 公告',
  'settings.announceToAgentHint': '关闭后系统提示词不再介绍本插件。',
  'settings.dshCommand': 'dsh 命令',
  'settings.dshCommandHint': '启动器调用的命令，需在 PATH 中（默认 dsh）。',
  'settings.url': 'Web GUI 地址',
  'settings.urlHint': '启动后等待就绪并打开的地址（默认 http://127.0.0.1:3080）。',
  'settings.profile': '启动 profile（可选）',
  'settings.profileHint': '留空表示不带 --profile 参数启动 dsh web。',
  'settings.iconPath': '图标文件（可选）',
  'settings.iconPathHint': '桌面图标的 .ico/.png 路径；留空使用内置的 dsh 图标。',
  'settings.create': '创建桌面图标',
  'settings.creating': '创建中…',
  'settings.created': '桌面图标已创建',
  'settings.createFailed': '创建桌面图标失败',
  'settings.requireEnabled': '开启「启用插件」并保存后，此按钮可用。',
  'settings.warning': '注意',
  'settings.shutdownSection': '一键关机',
  'settings.confirmShutdown': '退出前确认',
  'settings.confirmShutdownHint': '关闭后点击按钮直接退出，不再弹出确认框。',
  'entry.label': '退出 DeepSeek Harness',
  'dialog.title': '确认退出',
  'dialog.description': '关闭 DeepSeek Harness 会结束 dsh web 进程，正在运行的会话、任务与未保存状态可能中断。确定要退出吗？',
  'dialog.confirm': '退出',
  'dialog.cancel': '取消',
  'dialog.shuttingDown': '正在退出…',
  'dialog.failed': '退出请求失败：{message}',
  'dialog.retry': '重试',
  'dialog.close': '关闭',
  'settings.overridden': '已覆盖',
  'settings.reset': '恢复默认',
  'settings.notExposed': '当前 DSH 版本未向设置页暴露本插件的配置命名空间，表单不可用。可编辑 $DSH_HOME/settings.yaml 直接配置，或确认提供该命名空间的插件已挂载其设置域并重启。',
  'settings.readOnly': '当前部署的设置只读。',
  'settings.inherit': '继承',
  'settings.on': '开',
  'settings.off': '关',
  'settings.expand': '展开设置',
  'settings.collapse': '收起设置',
  'settings.save': '保存',
  'settings.saving': '保存中…',
  'settings.discard': '放弃',
  'settings.unsaved': '未保存',
  'settings.saveFailed': '部署未接受这些值，已保留供你修改。',
  'settings.invalidNumber': '请输入数字，留空则使用默认值。',
} satisfies Record<string, string>

/** The desktop-launcher key union. */
export type DesktopLauncherKey = keyof typeof zh

/** English dictionary, checked complete against the zh key set. */
export const en = {
  'settings.title': 'Desktop launcher',
  'settings.description': 'Create desktop icon and one-click shutdown.',
  'settings.enabled': 'Enable plugin',
  'settings.enabledHint': 'When off, desktop icon creation and the shutdown button stop.',
  'settings.announceToAgent': 'Announce to agent',
  'settings.announceToAgentHint': 'When off, the system prompt no longer introduces this plugin.',
  'settings.dshCommand': 'dsh command',
  'settings.dshCommandHint': 'Command the launcher calls; it must be on PATH (default dsh).',
  'settings.url': 'Web GUI URL',
  'settings.urlHint': 'Address the launcher waits for and opens (default http://127.0.0.1:3080).',
  'settings.profile': 'Startup profile (optional)',
  'settings.profileHint': 'Leave blank to start dsh web without a --profile argument.',
  'settings.iconPath': 'Icon file (optional)',
  'settings.iconPathHint': 'Path to a .ico/.png for the desktop icon; blank uses the bundled dsh icon.',
  'settings.create': 'Create desktop icon',
  'settings.creating': 'Creating…',
  'settings.created': 'Desktop icon created',
  'settings.createFailed': 'Failed to create desktop icon',
  'settings.requireEnabled': 'Enable the plugin and save to use this button.',
  'settings.warning': 'Note',
  'settings.shutdownSection': 'One-click shutdown',
  'settings.confirmShutdown': 'Confirm before exit',
  'settings.confirmShutdownHint': 'When off, the button exits immediately without a confirm dialog.',
  'entry.label': 'Exit DeepSeek Harness',
  'dialog.title': 'Confirm exit',
  'dialog.description': 'Closing DeepSeek Harness ends the dsh web process; running sessions, tasks and unsaved state may be interrupted. Are you sure you want to exit?',
  'dialog.confirm': 'Exit',
  'dialog.cancel': 'Cancel',
  'dialog.shuttingDown': 'Exiting…',
  'dialog.failed': 'Exit request failed: {message}',
  'dialog.retry': 'Retry',
  'dialog.close': 'Close',
  'settings.overridden': 'Overridden',
  'settings.reset': 'Reset to default',
  'settings.notExposed': 'This DSH version does not expose this plugin\'s settings namespace to the configuration page, so the form is unavailable. Edit $DSH_HOME/settings.yaml directly, or confirm that the plugin owning the namespace is mounted with its settings domain and restart.',
  'settings.readOnly': 'This deployment stores settings read-only.',
  'settings.inherit': 'Inherit',
  'settings.on': 'On',
  'settings.off': 'Off',
  'settings.expand': 'Show settings',
  'settings.collapse': 'Hide settings',
  'settings.save': 'Save',
  'settings.saving': 'Saving…',
  'settings.discard': 'Discard',
  'settings.unsaved': 'Unsaved',
  'settings.saveFailed': 'The deployment did not accept these values; they were left for you to correct.',
  'settings.invalidNumber': 'Enter a number, or leave blank to use the default.',
} satisfies Record<DesktopLauncherKey, string>
